import { Directive, OnDestroy } from '@angular/core';
import { sum } from 'lodash-es';
import { Observable, combineLatest, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { pauseResumeOperator } from '@nimbus-library/features/shared/operators/pausable.operator';
import { AudioService } from '@nimbus-library/features/shared/services/media/audio.service';
import { IAudioService, PlaybackState } from '@nimbus-library/features/shared/services/media/i-audio.service';

@Directive()
export class PlaylistAudioService implements IAudioService, OnDestroy {
  private audioServicesList: AudioService[];
  private checkChanges$: BehaviorSubject<boolean>;

  private set checkChanges(value: boolean) {
    this.checkChanges$.next(value);
  }

  private currentIndex: number;

  public elapsedSeconds$: Observable<number>;
  public duration$: Observable<number>;
  public loaded$: Observable<boolean>;
  public state$: Observable<PlaybackState>;

  public constructor(urls: string[], createAudioService: (url: string) => AudioService) {
    this.checkChanges$ = new BehaviorSubject<boolean>(true);
    this.currentIndex = 0;

    this.audioServicesList = urls.map((url) => createAudioService(url));

    this.elapsedSeconds$ = combineLatest(this.audioServicesList.map((svc) => svc.elapsedSeconds$)).pipe(
      pauseResumeOperator(this.checkChanges$),
      map((elapsed) => sum(elapsed) || 0),
    );
    this.duration$ = combineLatest(this.audioServicesList.map((svc) => svc.duration$)).pipe(map((duration) => sum(duration)));
    this.loaded$ = combineLatest(this.audioServicesList.map((svc) => svc.loaded$)).pipe(map((loaded) => loaded.every((l) => l)));
    this.state$ = combineLatest(this.audioServicesList.map((svc) => svc.state$)).pipe(
      pauseResumeOperator(this.checkChanges$),
      map((state) => state[this.currentIndex]),
      map((state) => {
        if (state === PlaybackState.Finish) {
          this.currentIndex++;
          if (this.currentIndex !== this.audioServicesList.length) {
            this.checkChanges = false;
            this.play();
            this.checkChanges = true;

            return PlaybackState.Play;
          }
        }

        return state;
      }),
    );
  }

  private get currentService(): AudioService {
    return this.audioServicesList[this.currentIndex];
  }

  public ngOnDestroy(): void {
    for (const svc of this.audioServicesList) {
      svc.ngOnDestroy();
    }
  }

  public play(): void {
    if (this.currentIndex === this.audioServicesList.length) {
      this.reset();
    }

    this.currentService.play();
  }

  public pause(): void {
    this.currentService.pause();
  }

  public reset(): void {
    this.checkChanges = false;
    while (this.currentIndex > 0) {
      this.currentService && this.currentService.reset();

      this.currentIndex--;
    }
    if (this.currentService) {
      this.currentService.reset();
    }

    this.checkChanges = true;
  }

  public setCurrentTime(seconds: number): void {
    this.checkChanges = false;

    const wasPlaying = this.currentService && this.currentService.state$.value === PlaybackState.Play;

    let newIndex: number;
    let tracksDuration = 0;
    for (let i = 0; i < this.audioServicesList.length; i++) {
      tracksDuration += this.audioServicesList[i].duration$.value;
      if (seconds <= tracksDuration) {
        tracksDuration -= this.audioServicesList[i].duration$.value;
        newIndex = i;
        break;
      } else {
        this.audioServicesList[i].finish();
      }
    }

    const trackChanged = newIndex !== this.currentIndex;
    if (newIndex < this.currentIndex) {
      for (let i = Math.min(this.currentIndex, this.audioServicesList.length - 1); i > newIndex; i--) {
        this.audioServicesList[i].reset();
      }
    }

    this.currentIndex = newIndex;

    const trackSeconds = seconds - tracksDuration;
    this.currentService.setCurrentTime(trackSeconds);

    if (trackChanged) {
      if (wasPlaying) {
        this.currentService.play();
      } else {
        if (this.currentService.state$.value === PlaybackState.Finish) {
          this.currentService.state$.next(PlaybackState.Pause);
        }
      }
    }

    this.checkChanges = true;
  }
}
